/*
 * Copyright (C) Schweizerische Bundesbahnen SBB, 2018.
 */

import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../config/config.service';
import { QueryService } from './query.service';
import { CONSTANTS } from 'src/app/config/constants.module';
import { map } from 'rxjs/operators';

/**
 * @copyright ESTA-GIS: Schweizerische Bundesbahnen SBB, 2017
 * @class LayerQueryService
 * @version 0.0.1
 * @since März 2018
 * @author Tobias Wyss
 * @description Provides the possibility to query services
 */
@Injectable({
  providedIn: 'root',
})
export class LayerQueryService {
  constructor(
    private _configService: ConfigService,
    private _queryService: QueryService,
    @Inject(CONSTANTS) private CONST: any
  ) {}

  /**
   * gets Feature from specified layer.
   * @param {string} layerId Layer to read features from
   * @param {string} whereQuery specify a where (e.g. 1=1)
   * @param {string[]} outFields specify the outfields as a String array
   * @returns {Observable<any>} the found records
   */
  public featuresFromLayer(
    layerId: string,
    whereQuery: string,
    outFields: string[]
  ): Observable<any> {
    let feature;
    const resultObservable: Observable<any> = this.query(
      this._configService.getLayerUrl(layerId),
      whereQuery,
      outFields
    ).pipe(
      map(result => {
        if (result.features?.length > 0) {
          feature = result;
        }
      })
    );

    return resultObservable.pipe(
      map(() => {
        return feature;
      })
    );
  }

  public query(
    layerUrl: string,
    whereQuery: string,
    outFields: string[],
    orderByFields?: string[],
    requiredGeometry = false
  ): Observable<any> {
    const errorCode = this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    const query: __esri.Query = this._queryService.buildQuery(
      requiredGeometry,
      whereQuery,
      outFields,
      orderByFields
    );
    return this._queryService.execute(layerUrl, query, errorCode).pipe(
      map(result => {
        return result;
      })
    );
  }

  /**
   * gets information/data from Gsharp Service based on x/y of given graphic
   * @param {__esri.Graphic} graphic Graphic with x/y
   * @param {string} layerId layerId to query
   * @returns {Observable<any>} result of request
   */
  public getInfosByGraphicsXY(graphic: __esri.Graphic, layerId: string): Observable<any> {
    let feature;
    const layerUrl: string = this._configService.getLayerUrl(layerId);
    const resultObservable: Observable<any> = this.intersectQuery(layerUrl, graphic.geometry).pipe(
      map(result => {
        if (result.features?.length > 0) {
          feature = result;
        }
      })
    );

    return resultObservable.pipe(
      map(() => {
        return feature;
      })
    );
  }

  private intersectQuery(layerUrl: string, geometry: __esri.Geometry): Observable<any> {
    const errorCode = this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    const query: __esri.Query = this._queryService.buildIntersectsQuery(geometry, true);
    return this._queryService.execute(layerUrl, query, errorCode);
  }
}
